import React, { FC } from "react";
import { SparksSummary } from "@shared/models";
import { Button, Icon } from "@shared/components";

import "./index.scss";

interface SparksCountersProps {
  summary: SparksSummary;
  // redeem-tab-plan.md PR2 §2a: REQUIRED, not optional. The "Redeem Sparks →" button's action differs
  // by consumer (MySparksContainer navigates to the Redeem tab; RedeemContainer scrolls to its own
  // catalog block below), so this component never bakes one in — an optional handler would let a third
  // consumer render the button with no action and still compile.
  onRedeemClick: () => void;
}

/**
 * Presentational. Renders the three counters the redesign's stat strip draws (redeem-tab-plan.md PR2
 * §2a): Sparks all time (`lifetimeEarned`), Ready to redeem (`redeemable`), and Pending HR approval
 * (`pendingApproval`, PR1 §1a) — plus the "Redeem Sparks →" button on the right, whose behaviour is
 * entirely owned by the parent via `onRedeemClick`.
 *
 * `redeemed` and the derived merch-reward line (`merchUnits`/`rewardUnit`) are no longer rendered here
 * — the redesign drops both from the strip. `merchUnits`/`rewardUnit` still exist on `SparksSummary`
 * (the api still sends them), but as of this change NOTHING in this repo reads either field anymore;
 * the load-bearing guard their old doc comment carried (never recompute `merchUnits` client-side,
 * since a negative `redeemable` or `rewardUnit === 0` would disagree with the server's own value) has
 * no code left to protect and is recorded on the model instead.
 *
 * Structural fidelity fix (Figma `SPARK Redeem · v2`): the three stats and the button now share ONE
 * bordered/tinted panel (`.sparks-counters-panel`) instead of three separately-boxed cards with the
 * button outside them. `data-testid`s are unchanged (`sparks-counter-lifetime` etc.), still on each
 * stat's own wrapper, so existing `within()`-scoped assertions keep working unmodified.
 *
 * Design fidelity fix: each stat's icon tile sits to the LEFT of its value/caption column, not above
 * it — `.sparks-counter-stat` is a row (icon, then a `.sparks-counter-stat-text` column of
 * value-then-label), matching the design's "[icon] 370 / Sparks all time" layout. The three stats stay
 * siblings in the same left-to-right order (lifetime, redeemable, pending approval) — an existing test
 * pins that order.
 */
const SparksCounters: FC<SparksCountersProps> = (props) => {
  const { summary, onRedeemClick } = props;
  const { lifetimeEarned, redeemable, pendingApproval } = summary;

  return (
    <div className="sparks-counters">
      <div className="sparks-counters-panel">
        <div className="sparks-counters-stats">
          <div className="sparks-counter-stat sparks-counter-stat--lifetime" data-testid="sparks-counter-lifetime">
            {/* aria-hidden stays on the tile: the value + label below already say "370 / Sparks all
                time", so the glyph is pure decoration. Tile is white — `zap` is drawn for a white
                background (index.scss's load-bearing colour-pairing note). */}
            <div className="sparks-counter-stat-icon" aria-hidden="true">
              <Icon type="zap" />
            </div>
            <div className="sparks-counter-stat-text">
              <div className="sparks-counter-stat-value">{lifetimeEarned}</div>
              <div className="sparks-counter-stat-label">Sparks all time</div>
            </div>
          </div>
          <div className="sparks-counters-divider" aria-hidden="true" />
          <div className="sparks-counter-stat sparks-counter-stat--redeemable" data-testid="sparks-counter-redeemable">
            {/* `--accent` (pink) tile is required here: `wallet` is stroked white and is invisible on
                any other background (index.scss's load-bearing colour-pairing note). */}
            <div className="sparks-counter-stat-icon" aria-hidden="true">
              <Icon type="wallet" />
            </div>
            <div className="sparks-counter-stat-text">
              <div className="sparks-counter-stat-value">{redeemable}</div>
              <div className="sparks-counter-stat-label">Ready to redeem</div>
            </div>
          </div>
          <div className="sparks-counters-divider" aria-hidden="true" />
          <div
            className="sparks-counter-stat sparks-counter-stat--pending"
            data-testid="sparks-counter-pending-approval"
          >
            {/* Tile is white — `clock` is drawn for a white background (index.scss's load-bearing
                colour-pairing note). */}
            <div className="sparks-counter-stat-icon" aria-hidden="true">
              <Icon type="clock" />
            </div>
            <div className="sparks-counter-stat-text">
              <div className="sparks-counter-stat-value">{pendingApproval}</div>
              <div className="sparks-counter-stat-label">Pending HR approval</div>
            </div>
          </div>
        </div>
        <Button className="sparks-redeem-button" onClick={onRedeemClick}>
          Redeem Sparks →
        </Button>
      </div>
    </div>
  );
};

export default SparksCounters;
